import { parseGameLayers, type GameLayer } from '@b1fr0st/contracts/game-layers';
import { env } from '@/env.js';
import { cardTile, libraryImage } from '@/components/lobby/tileImage.js';
import type { OriginalsCatalogEntry } from './useOriginalsCatalog.js';

/**
 * The picture a game's small badge shows, taken from its lobby card: `hero` is
 * the card's main object on a transparent background (drawn whole), `tile` the
 * whole card (cropped to its top, where the object sits above the name).
 */
export interface OriginalCardIcon {
  kind: 'hero' | 'tile';
  src: string;
  srcSet?: string;
}

/**
 * The card's main object: of the layers in front of the backdrop (layer 0),
 * the one at depth 1, or the nearest to it. Deeper layers are sparkles and
 * glows that float over the object, shallower ones scenery behind it.
 */
export function heroLayer(layers: readonly GameLayer[]): GameLayer | null {
  let best: GameLayer | null = null;
  for (const layer of layers.slice(1)) {
    if (!best || Math.abs(layer.depth - 1) < Math.abs(best.depth - 1)) best = layer;
  }
  return best;
}

/**
 * What a game's badge of `cssSize` pixels shows from its catalog row: the
 * card's hero layer, else the theme card due at `now`, else its Tile, else
 * null (the caller's line icon).
 */
export function originalCardIcon(
  row: Pick<OriginalsCatalogEntry, 'layers' | 'tile' | 'themeTiles'> | undefined,
  cssSize: number,
  now: Date,
  origin: string = env.VITE_IMG_ORIGIN,
): OriginalCardIcon | null {
  if (!row) return null;
  const layers = parseGameLayers(row.layers);
  const hero = layers ? heroLayer(layers) : null;
  const tile = hero ? null : cardTile(row, now, origin);
  const url = hero?.url ?? tile?.url;
  if (!url) return null;
  return { kind: hero ? 'hero' : 'tile', ...(libraryImage(url, cssSize, origin) ?? { src: url }) };
}
